import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router, RouterModule } from '@angular/router';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { map, startWith, combineLatest, Subject, BehaviorSubject } from 'rxjs';
import { MOBILE_BREAKPOINT } from 'src/app/shared/breakpoints';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { VehicleWithRestrictions } from 'src/app/models/vehicle-with-restrictions';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

type Row = VehicleWithRestrictions['restrictions'][number] & {
  vehicleName: string;
  vinNo: string;          
};

type SortMeta = { field: string; order: 1 | -1 };

@Component({
  selector: 'app-vehicles-table',
  standalone: true,
  imports: [
    CommonModule, AsyncPipe, RouterModule,
    TableModule, RippleModule, IconFieldModule, InputIconModule, InputTextModule,
    FormsModule, ReactiveFormsModule, CardModule, ButtonModule, PopoverModule, TooltipModule
  ],
  templateUrl: './vehicles-table.component.html',
  styleUrls: ['./vehicles-table.component.scss']
})
export class VehiclesTableComponent {
  @ViewChild('tbl') tbl!: Table;

  // paging
  rowsPerPage = 12;

  // search
  private search$ = new Subject<string>();

  // PrimeNG multi-sort: keep group field first
  sortMeta: SortMeta[] = [{ field: 'vehicleName', order: 1 }];
  rows$ = combineLatest([
    this.restrictionsService.getAllVehiclesWithRestrictions$(),
    this.search$.pipe(startWith(''))
  ]).pipe(
    map(([vehicles, q]) => {
      const query = (q ?? '').toLowerCase();
      const flat: Row[] = vehicles.flatMap(v =>
        (v.restrictions ?? []).map(r => ({
          ...r,
          vehicleName: v.name,
          vinNo: v.vinNo
        }))
      );
      if (!query) return flat;
      return flat.filter(r =>
        r.vehicleName.toLowerCase().includes(query) ||
        String(r.displayName ?? '').toLowerCase().includes(query) ||
        r.restrictionName.toLowerCase().includes(query) ||
        r.responsible.toLowerCase().includes(query)
      );
    })
  );

  public searchTermControl = new FormControl<string>('')
  public searchTerm = ""

  public sortOrderSubject = new BehaviorSubject<boolean>(true)

  public filteredAndSortedVehicles$ = combineLatest({
    vehicles: this.restrictionsService.getAllVehiclesWithRestrictions$(),
    searchTerm: this.searchTermControl.valueChanges.pipe(startWith('')),
    sortOrder: this.sortOrderSubject.asObservable()
  }).pipe(
    map(source => {

      this.searchTerm = source.searchTerm!

      let vehicles = source.vehicles ?? []

      if(vehicles.length && source.searchTerm) {
        const searchLower = source.searchTerm.toLowerCase();
          const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, ''); // Remove spaces
          const searchNormalized = normalize(searchLower);
        
          vehicles = vehicles.filter(v => {
            const vehicleMatch =
              normalize(v.name).includes(searchNormalized) ||
              normalize(v.vinNo ?? v.vehicleId).includes(searchNormalized); // use vinNo if available

            const restrictionMatch = (v.restrictions ?? []).some(r =>
              normalize(r.displayName).includes(searchNormalized) ||
              normalize(r.restrictionName).includes(searchNormalized)
            );

            return vehicleMatch || restrictionMatch;
        });
      }

      if(vehicles.length) {
        vehicles.sort((a, b) => {
          try {
            // Ensure names are strings, fallback to empty strings if undefined or non-strings
            const nameA = a && typeof a.name === 'string' ? a.name.toLowerCase() : '';
            const nameB = b && typeof b.name === 'string' ? b.name.toLowerCase() : '';

            if (source.sortOrder) {
              return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            } else {
              return nameA > nameB ? -1 : nameA < nameB ? 1 : 0;
            }
          } catch (error) {
            console.error("Error during sorting:", error, a, b);
            return 0; // Leave items in the same order if an error occurs
          }
        })
      }

      return vehicles
    })
  )

  isMobile$ = this.breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(r => r.matches));

  constructor(
    private restrictionsService: RestrictionsService,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {}

  sortWithinGroups(e: any) {
    const data = e.data as Row[];
    const field = e.field as keyof Row;
    const order = (e.order ?? 1) as 1 | -1;

    data.sort((a, b) => {
      // keep groups together by vehicleName; allow reversing when Vehicle header is clicked
      const nameOrder = field === 'vehicleName' ? order : 1;
      const g = a.vehicleName.localeCompare(b.vehicleName) * nameOrder;
      if (g !== 0) return g;

      // when another column is clicked, sort inside each group by that column
      if (field !== 'vehicleName') {
        const av = (a as any)[field];
        const bv = (b as any)[field];
        return (av < bv ? -1 : av > bv ? 1 : 0) * order;
      }
      return 0;
    });
  }

  public toggleSortOrder(): void {
    this.sortOrderSubject.next(!this.sortOrderSubject.value)
  }

  onGlobalFilter(value: string) {
    this.search$.next(value ?? '');
  }

  // Ensure the primary sort ALWAYS stays as vehicleName so groups remain contiguous.
  onSort(e: { field?: string; order?: 1 | -1 }) {
    const f = e.field ?? 'vehicleName';
    const o = (e.order ?? 1) as 1 | -1;

    this.sortMeta = [{ field: 'vehicleName', order: 1 }];
    if (f !== 'vehicleName') this.sortMeta.push({ field: f, order: o });

    this.tbl.sortMultiple();
  }

  goVehicle(vehicleName: string) {
    this.router.navigate(['vehicles', vehicleName]);
  }

  public getVehicleRestrictionStatusClass(vehicle: VehicleWithRestrictions) {
    const hasTwoBans = vehicle.restrictions.some(
      r => r.banPublicRoad && r.banTestTrack
    );

    if (hasTwoBans) return 'two-bans';

    const hasOneBan = vehicle.restrictions.some(
      r =>
        (r.banPublicRoad && !r.banTestTrack) ||
        (!r.banPublicRoad && r.banTestTrack)
    );

    if (hasOneBan) return 'one-ban';

    return 'no-ban';
  }

  public getVehicleCommentsHtml(vehicle: VehicleWithRestrictions): string {
    if (!vehicle?.restrictions?.length) return '<p>No additional information</p>';

    const comments = vehicle.restrictions
      .map(r => {
        const text = r.additionalInformation?.trim();
        if (!text) return null;
        return text.length > 150 ? text.slice(0, 150) + '…' : text;
      })
      .filter(Boolean);

    if (!comments.length) return '<p>No additional information</p>';

    return comments.join('<hr style="border: 0; border-top: 1px solid var(--border-color); margin: 6px 0;">');
  }
}
